import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Plus, Cube, Trash, Tag, X as XIcon } from "@phosphor-icons/react";

export default function Products() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: p } = await api.get("/products");
      // Backend returns {products:[...], count} — unwrap defensively
      setItems(Array.isArray(p) ? p : (p?.products || []));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((p) =>
    !q || p.name?.toLowerCase().includes(q.toLowerCase()) ||
    p.sku?.toLowerCase().includes(q.toLowerCase()) ||
    p.category?.toLowerCase().includes(q.toLowerCase()) ||
    p.brand?.toLowerCase().includes(q.toLowerCase())
  );

  const remove = async (id) => {
    if (!window.confirm("Delete this product? It will move to the recycle bin.")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Library</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Suspect Products</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">{items.length} SKUs · authentication markers, indicators &amp; references</div>
        </div>
        <button data-testid="add-product-btn" onClick={() => setShowAdd(true)} className="span-btn">
          <Plus size={14} /> Add product
        </button>
      </div>

      <div className="hairline px-3 h-10 surface flex items-center w-full max-w-md">
        <input
          data-testid="product-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, SKU, brand, category…"
          className="bg-transparent w-full text-[13px] outline-none"
        />
      </div>

      {error && (
        <div className="card-flat p-4 text-[12px] text-[var(--status-danger)]" data-testid="products-error">
          Could not load products: {String(error)}
        </div>
      )}

      <div className="card-flat">
        <table className="span-table">
          <thead><tr><th>Name</th><th>SKU</th><th>Brand</th><th>Category</th><th>Security Features</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-[12px] text-[var(--span-text-2)]">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-[12px] text-[var(--span-text-2)]">No products. Add your first SKU.</td></tr>
            )}
            {!loading && filtered.map((p) => (
              <tr key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                <td className="font-medium flex items-center gap-2"><Cube size={14}/> {p.name}</td>
                <td className="font-mono text-[12px] text-[var(--span-text-2)]">{p.sku || "—"}</td>
                <td>{p.brand || "—"}</td>
                <td><span className="badge">{p.category || "—"}</span></td>
                <td className="text-[12px]">
                  {(p.security_features || []).slice(0, 3).map((m, i) => (
                    <span key={i} className="badge text-[10px] mr-1"><Tag size={10}/> {m}</span>
                  ))}
                  {(p.security_features || []).length > 3 && <span className="text-[10px] text-[var(--span-text-2)]">+{p.security_features.length - 3}</span>}
                </td>
                <td className="text-[var(--span-text-2)]">{fmtDate(p.created_at)}</td>
                <td>
                  <button data-testid={`delete-product-${p.product_id}`} onClick={() => remove(p.product_id)} className="text-[var(--span-text-2)] hover:text-[var(--span-danger)]">
                    <Trash size={14}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", sku: "", brand: "", category: "general", notes: "", security_features: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.brand) {
      alert("Name and Brand are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/products", {
        name: form.name,
        brand: form.brand,
        sku: form.sku || null,
        category: form.category || "general",
        notes: form.notes || null,
        security_features: form.security_features.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onSaved();
    } catch (e2) {
      alert("Could not save: " + (e2.response?.data?.detail || e2.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-product-modal">
      <form onSubmit={submit} className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow">New product</div>
          <button type="button" onClick={onClose}><XIcon size={16}/></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-1">Name *</label>
            <input data-testid="product-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="span-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Brand *</label>
              <input data-testid="product-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} className="span-input" placeholder="e.g. Nike, Apple, SPAN client name" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">SKU</label>
              <input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="span-input"/>
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Category</label>
            <input value={form.category} onChange={(e) => set("category", e.target.value)} className="span-input" placeholder="e.g. footwear, electronics, FMCG"/>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Security features (comma-separated)</label>
            <input value={form.security_features} onChange={(e) => set("security_features", e.target.value)} placeholder="QR-NFC-Hologram, Batch-Code, Lot-Number" className="span-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="span-input"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="span-btn span-btn-ghost">Cancel</button>
          <button data-testid="submit-product-btn" disabled={busy} className="span-btn"><Plus size={12}/> Save</button>
        </div>
      </form>
    </div>
  );
}
